// ===== Agent Conversation Picker (Phase 1 — 정적 표면·오버레이 계약) =====
// D14(docs/chat-work-detection-intake-notes.md:265-288) 요구: 에이전트 방 진입 시 자동 세션
// 재개 대신 방 내부 중간 화면(대화 목록 + 새 대화)을 보여준다. 이 모듈은 그 화면의 표시 제어
// (show/hide/isVisible)와 OverlayRegistry 연동만 담당한다 — 진입 트리거(방 진입 시 자동 호출),
// 목록 데이터 요청, 새 대화 배선은 이 phase 범위 밖(Phase 2/3)이라 이 phase 완료 시점엔 아무도
// show()를 호출하지 않아 픽커는 hidden 상태로 남는다.

interface Window {
  AgentConversationPicker: {
    show: (onCancel?: () => void) => void;
    hide: () => void;
    cancel: () => void;
    isVisible: () => boolean;
  };
}

(function () {
  const OVERLAY_ID = "agent-convo-picker";
  const DESKTOP_MIN_WIDTH = 768;
  let cancelHandler: (() => void) | null = null;

  function _picker(): HTMLElement | null {
    return document.getElementById("agentConvoPicker");
  }

  function isVisible(): boolean {
    const el = _picker();
    return !!el && !el.hidden;
  }

  // 터치 입력이거나 뷰포트가 좁으면 자동 포커스를 주지 않는다 — 모바일에서 열자마자
  // 키보드가 뜨는 것을 막는다.
  function _isDesktop(): boolean {
    if (typeof window === "undefined") return false;
    const isTouch = typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;
    return !isTouch && typeof window.innerWidth === "number" && window.innerWidth >= DESKTOP_MIN_WIDTH;
  }

  // show()마다 세션 목록 상태를 리셋한다 — 리셋 없이는 onMoreSessions가 클라
  // sessionListState.offset===0으로 replace/append를 판정(ws-handlers.ts)하므로 이전 열람의 잔여
  // offset이 남아 목록이 중복 append되고, isSearching 잔존 시 무한스크롤이 영구 비활성된다
  // (검색 IIFE 리셋 선례 미러, ARCH-R4/FE-R1).
  function _resetSessionList(): void {
    sessionListState.offset = 0;
    sessionListState.isSearching = false;
    sessionListState.hasMore = false;
    sessionListState.loading = true;

    const searchInput = document.getElementById("sessionSearch");
    if (searchInput instanceof HTMLInputElement) searchInput.value = "";

    if (wsClient && wsClient.isConnected) {
      wsClient.send("load-more-sessions", { offset: 0, limit: 15 });
      return;
    }

    // 미접속: 요청 대신 대기 상태를 표시한다. 이미 목록이 렌더되어 있으면 유지(불필요한 깜빡임 방지).
    const list = document.getElementById("sessionList");
    if (list && list.children.length === 0) {
      list.innerHTML = '<div class="session-empty">연결 대기 중...</div>';
    }
  }

  function show(onCancel?: () => void): void {
    const el = _picker();
    if (!el) return;
    cancelHandler = typeof onCancel === "function" ? onCancel : null;
    el.hidden = false;
    if (typeof OverlayRegistry !== "undefined") OverlayRegistry.register(OVERLAY_ID, cancel);
    _resetSessionList();
    if (_isDesktop()) {
      const searchInput = document.getElementById("sessionSearch");
      if (searchInput instanceof HTMLInputElement) searchInput.focus();
    }
  }

  function hide(): void {
    const el = _picker();
    if (!el || el.hidden) return;
    el.hidden = true;
    cancelHandler = null;
    if (typeof OverlayRegistry !== "undefined") OverlayRegistry.unregister(OVERLAY_ID);
  }

  // 닫기/Escape는 단순 hide와 다르다. 사용자가 아직 conversation을 선택하지 않았으므로
  // 에이전트 방 진입 직전의 방·헤더 상태를 호출자(room-list.ts)가 복원하도록 위임한다.
  function cancel(): void {
    const onCancel = cancelHandler;
    hide();
    if (onCancel) onCancel();
  }

  document.addEventListener("keydown", (event: KeyboardEvent) => {
    if (event.key === "Escape" && isVisible()) cancel();
  });

  document.getElementById("agentConvoCloseBtn")?.addEventListener("click", cancel);

  // D14(Phase 3): 새 대화는 기존 startNewConversation(chat-unread-badge.ts)에 위임한다 — 신규
  // newSession/resumeSession 콜사이트를 만들지 않는다(SSOT 짝은 그 함수 내부 검증이 유지).
  // typeof 가드는 bottom-nav-renderer.ts:246-248 선례를 미러한다.
  document.getElementById("agentConvoNewBtn")?.addEventListener("click", () => {
    if (typeof startNewConversation === "function") startNewConversation();
  });

  window.AgentConversationPicker = { show, hide, cancel, isVisible };
})();
